/* De vaste pagina's van de site (/crm/website/site/paginas, ADR 0024): de lijst, en het formulier van
 * één pagina op /crm/website/site/paginas/:id. Dit bestand tekent alleen. Welke pagina's de CRM kent,
 * wat de site per pagina met een kolom doet en wat er wordt geschreven, staat in data/paginas.ts.
 *
 * Er is geen knop om een pagina toe te voegen of te verwijderen, en de slug en de naam liggen vast:
 * de code van de site zoekt een pagina op haar slug. Een kolom die de site op een pagina niet toont,
 * staat er alleen-lezen met de reden erbij. Na het opslaan vraagt de CRM de site om te verversen
 * (ververs.ts, ADR 0031). */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ExternalLink, Save } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Gewijzigd } from './Aanvullingen';
import { usePaginaOpslaan, useRechten, useVastePagina, useVastePaginas } from './hooks';
import { VOORVOEGSEL, adresVanPad } from './data/media';
import {
  INHOUD, ONBEKEND, TEKENING_BESTAND, TEKENING_FOUT, VELDEN, controleerPagina, isBewerkbaar, kennisVan, leesInhoud, padOpSite, stroomLoopt, wijziging,
  zetTekening, zonderRij, type Gebruik, type PaginaKennis, type Veld,
} from './data/paginas';
import type { Rij } from './data/website';
import { Beeldveld } from './MediaKiezer';
import { meldOpslag } from './ververs';
import { Fout, Kop, LetOp, PAGINAS_PAD } from './WebsiteBeheer';

const SITE = 'greenvision.energy';
const LIJST = `/crm/website/site/${PAGINAS_PAD}`;
const TITEL = "Pagina's";

const naamVan = (rij: Rij): string => String(rij.title || rij.slug || '(zonder naam)');
/** Het adres zoals een bezoeker het typt: greenvision.energy/tools; de homepage zonder pad. */
const adresOpSite = (pad: string): string => `${SITE}${pad === '/' ? '' : pad}`;

/* ── De lijst ──────────────────────────────────────────────────────────────── */

export function WebsitePaginas() {
  const { data, error, isLoading } = useVastePaginas();
  const mist = data ? zonderRij(data) : [];
  return (
    <div className="space-y-4">
      <Kop titel={TITEL} terug={{ naar: '/crm/website/site', label: 'Content op de site' }} />
      <p className="max-w-3xl text-sm text-muted-foreground" data-uitleg="paginas">
        De vaste pagina&apos;s van de site. De meeste tekst van deze pagina&apos;s staat in de code van de site. Hier wijzig je de foto bovenaan, en bij de
        homepage en de rekentools ook de kop. Een pagina toevoegen of weghalen kan hier niet: de code van de site zoekt elke pagina op haar slug.
      </p>
      {error && <Fout tekst={(error as Error).message} />}
      {isLoading && <p className="text-sm text-muted-foreground">Laden…</p>}
      {data && data.length === 0 && <p className="text-sm text-muted-foreground">Nog niets.</p>}
      {data && data.length > 0 && (
        <Card>
          <CardContent className="divide-y p-0">
            {data.map((rij) => {
              const kennis = kennisVan(rij.slug);
              const pad = padOpSite(rij);
              const beeld = String(rij.hero_image ?? '');
              return (
                <Link key={rij.id} to={`${LIJST}/${rij.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50" data-rij={rij.id} data-slug={rij.slug}>
                  {beeld.startsWith(VOORVOEGSEL)
                    ? <img src={adresVanPad(beeld)} alt="" loading="lazy" decoding="async" className="h-10 w-16 shrink-0 rounded border bg-white object-cover" />
                    : <span className="h-10 w-16 shrink-0 rounded border bg-muted" aria-hidden />}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{naamVan(rij)}</div>
                    <div className="truncate text-xs text-muted-foreground">{pad ? adresOpSite(pad) : `slug: ${rij.slug}`}</div>
                  </div>
                  {!kennis && <Badge variant="secondary">Onbekend bij de CRM</Badge>}
                  {kennis && !pad && <Badge variant="secondary">Niet op de site</Badge>}
                </Link>
              );
            })}
          </CardContent>
        </Card>
      )}
      {mist.length > 0 && (
        <p className="max-w-3xl text-xs text-muted-foreground" data-zonder-rij>
          De site zoekt ook {mist.length === 1 ? 'een pagina' : "pagina's"} met de slug {mist.map((p) => p.slug).join(', ')}. Die {mist.length === 1 ? 'rij is' : 'rijen zijn'} er
          niet: daar staat de vaste foto uit de code van de site. Een rij toevoegen kan hier niet.
        </p>
      )}
    </div>
  );
}

/* ── Het formulier van één pagina ──────────────────────────────────────────── */

export function WebsitePagina() {
  const { id } = useParams();
  const nieuw = id === 'nieuw';
  const { magBewerken } = useRechten();
  const { data: rij, error, isLoading } = useVastePagina(nieuw ? undefined : id);
  const terug = { naar: LIJST, label: TITEL };

  if (nieuw) {
    return (
      <div className="space-y-4">
        <Kop titel={TITEL} terug={terug} />
        <Fout tekst="Een pagina maak je hier niet aan. Welke pagina's er zijn, staat in de code van de site." />
      </div>
    );
  }
  if (isLoading) return <p className="text-sm text-muted-foreground">Laden…</p>;
  if (error || !rij) {
    return (
      <div className="space-y-4">
        <Kop titel={TITEL} terug={terug} />
        <Fout tekst={error ? (error as Error).message : 'Pagina niet gevonden.'} />
      </div>
    );
  }
  return <Formulier key={rij.id} vorige={rij} magBewerken={magBewerken} />;
}

/** Eén kolom: een vak als de site hem op deze pagina toont, en anders wat er staat, alleen-lezen. */
function Vak({ veld, gebruik, waarde, fout, uit, zet }: { veld: Veld; gebruik: Gebruik; waarde: string; fout?: string; uit: boolean; zet: (waarde: string) => void }) {
  const id = `veld-${veld.kolom}`;
  if (!gebruik.toont) {
    return (
      <div className="space-y-1" data-veld={veld.kolom} data-veld-staat="alleen-lezen">
        <p className="text-sm font-medium">{veld.label} <Badge variant="secondary" className="ml-1">Alleen lezen</Badge></p>
        <p className="break-words text-sm text-muted-foreground">{waarde.trim() ? `Er staat: ${waarde}` : 'Er staat niets.'}</p>
        <p className="text-xs text-muted-foreground">{gebruik.uitleg}</p>
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-veld={veld.kolom} data-veld-staat="bewerkbaar">
      <Label htmlFor={id}>{veld.label}</Label>
      {veld.soort === 'afbeelding'
        ? <Beeldveld id={id} waarde={waarde} uit={uit} zet={zet} />
        : <Input id={id} value={waarde} disabled={uit} onChange={(e) => zet(e.target.value)} />}
      <p className="text-xs text-muted-foreground">{gebruik.uitleg}</p>
      {fout && <p className="text-xs text-destructive" role="alert">{fout}</p>}
    </div>
  );
}

/** De tekening op de homepage: een beeld uit `content`, met de bewegende laag die bij één bestand hoort. */
function Tekening({ kennis, vorige, concept, fout, uit, zet }: { kennis: PaginaKennis; vorige: Rij; concept: Rij; fout?: string; uit: boolean; zet: (content: unknown) => void }) {
  const opgeslagen = leesInhoud(kennis, vorige[INHOUD]);
  const lezing = opgeslagen.ok ? leesInhoud(kennis, concept[INHOUD]) : opgeslagen;
  if (!lezing.ok) {
    return (
      <Card data-kaart="tekening" data-kaart-staat="alleen-lezen">
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">De tekening op de homepage <Badge variant="secondary">Alleen lezen</Badge></CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            De inhoud van deze pagina heeft een vorm die de bewerker niet kent. {lezing.reden} Daarom is de tekening hier niet te wijzigen. De inhoud blijft bij opslaan ongewijzigd.
          </p>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border p-3 text-xs text-muted-foreground">{JSON.stringify(vorige[INHOUD] ?? null, null, 2)}</pre>
        </CardContent>
      </Card>
    );
  }
  const gewijzigd = lezing.tekening.trim() !== opgeslagen.tekening.trim();
  const loopt = stroomLoopt(lezing.tekening);
  return (
    <Card data-kaart="tekening" data-kaart-staat={gewijzigd ? 'gewijzigd' : 'ongewijzigd'}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">De tekening op de homepage</CardTitle>
          {gewijzigd && <Gewijzigd uit={uit} terug={() => zet(vorige[INHOUD])} />}
        </div>
        <p className="text-sm text-muted-foreground">
          Het beeld naast de tekst over de integrale energieoplossingen, halverwege de homepage. Leeg betekent: de site toont daar het beeld van de eerste energieoplossing.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="veld-tekening">Beeld</Label>
          <Beeldveld id="veld-tekening" waarde={lezing.tekening} uit={uit} zet={(pad) => zet(zetTekening(concept[INHOUD], pad))} />
          {fout && <p className="text-xs text-destructive" role="alert">{fout}</p>}
        </div>
        <p className="text-xs text-muted-foreground">
          Over deze tekening loopt op de site een bewegende laag: de stroom over de kabels. Die laag is opgemeten op één bestand en hoort alleen daarbij: <code className="break-all">{TEKENING_BESTAND}</code>
        </p>
        {loopt
          ? <p className="text-sm" data-stroom="aan">Met dit beeld loopt de stroom over de tekening.</p>
          : (
            <div data-stroom="uit">
              <LetOp sleutel="stroom" tekst={lezing.tekening.trim()
                ? 'Let op: met dit beeld staat de beweging uit. De site toont de bewegende laag alleen bij het bestand hierboven. Bij elk ander beeld staat er alleen het beeld, zonder stroom.'
                : 'Let op: zonder beeld staat de beweging uit. De site toont dan het beeld van de eerste energieoplossing, zonder stroom.'} />
            </div>
          )}
      </CardContent>
    </Card>
  );
}

function Formulier({ vorige, magBewerken }: { vorige: Rij; magBewerken: boolean }) {
  const { toast } = useToast();
  const [concept, setConcept] = useState<Rij>(vorige);
  const opslaan = usePaginaOpslaan();
  const kennis = kennisVan(vorige.slug);
  const bewerkbaar = isBewerkbaar(kennis);
  const uit = !magBewerken || opslaan.isPending;
  const pad = padOpSite(vorige);

  /* Alleen overnemen als de server een nieuwere versie heeft (na opslaan). Een verversing op de
   * achtergrond met dezelfde inhoud mag niet weggooien wat iemand net heeft getypt. */
  const versie = `${vorige.id}:${vorige.updated_at ?? ''}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setConcept(vorige); }, [versie]);

  const controle = useMemo(() => controleerPagina(vorige, concept), [vorige, concept]);
  const gewijzigd = useMemo(() => Object.keys(wijziging(vorige, concept)).length > 0, [vorige, concept]);
  const zet = (kolom: string, waarde: unknown) => setConcept((c) => ({ ...c, [kolom]: waarde }));
  /* Wat er in `content` staat en hier niet te bewerken is. Bij een onbekende vorm is dat alles; op de
   * homepage staat het dan al bij de tekening. */
  const inhoud = leesInhoud(kennis, vorige[INHOUD]);
  const rest = inhoud.ok ? inhoud.rest : bewerkbaar && kennis.tekening ? null : vorige[INHOUD];

  const bewaar = async () => {
    try {
      const uitkomst = await opslaan.mutateAsync({ vorige, concept });
      // Wat er nu in de database staat, is het nieuwe begin: het formulier is weer ongewijzigd.
      setConcept(uitkomst.rij);
      void meldOpslag(toast, uitkomst.verversing, 'Opgeslagen');
    } catch (fout) {
      toast({ title: 'Niet opgeslagen', description: fout instanceof Error ? fout.message : String(fout), variant: 'destructive' });
    }
  };

  const knop = magBewerken && bewerkbaar && (
    <Button onClick={bewaar} disabled={opslaan.isPending || !gewijzigd || !controle.ok} size="sm">
      <Save className="mr-1 h-4 w-4" /> {opslaan.isPending ? 'Opslaan…' : 'Opslaan'}
    </Button>
  );

  return (
    <div className="space-y-4" data-pagina={vorige.slug}>
      <Kop titel={naamVan(vorige)} terug={{ naar: LIJST, label: TITEL }}>
        {pad && (
          <a href={`https://${SITE}${pad}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            Bekijk op de site <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        {knop}
      </Kop>

      <p className="max-w-3xl text-sm text-muted-foreground" data-uitleg="pagina">
        {pad ? `Adres op de site: ${adresOpSite(pad)}. ` : ''}Slug: <code>{String(vorige.slug)}</code>. De slug en de naam liggen vast: de code van de site zoekt een pagina op de slug, en
        toont de naam nergens.{bewerkbaar ? ' De rest van de tekst van deze pagina staat in de code van de site.' : ''}
        {bewerkbaar && kennis.uitleg ? ` ${kennis.uitleg}` : ''}
      </p>
      {!kennis && <LetOp sleutel="onbekend" tekst={ONBEKEND} />}
      {kennis && !bewerkbaar && <LetOp sleutel="niet-op-de-site" tekst={kennis.uitleg ?? ONBEKEND} />}
      {bewerkbaar && !magBewerken && <Fout tekst="Je kunt deze content lezen, maar niet wijzigen." />}

      <Card data-kaart="bovenaan">
        <CardHeader className="pb-2"><CardTitle className="text-base">Bovenaan de pagina</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          {VELDEN.map((veld) => (
            <Vak key={veld.kolom} veld={veld} waarde={String(concept[veld.kolom] ?? '')} fout={controle.fouten[veld.kolom]} uit={uit}
              gebruik={kennis ? kennis.velden[veld.kolom] : { toont: false, uitleg: 'De CRM weet niet of de site dit toont.' }} zet={(w) => zet(veld.kolom, w)} />
          ))}
        </CardContent>
      </Card>

      {bewerkbaar && kennis.tekening && (
        <Tekening kennis={kennis} vorige={vorige} concept={concept} fout={controle.fouten[TEKENING_FOUT]} uit={uit} zet={(content) => zet(INHOUD, content)} />
      )}

      {/* De knop staat ook onderaan: met de tekening erbij is het formulier lang. */}
      {knop && kennis?.tekening && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {!controle.ok && <p className="text-xs text-destructive">{Object.values(controle.fouten)[0]}</p>}
          {knop}
        </div>
      )}

      {rest != null && (
        <Card data-kaart="inhoud" data-kaart-staat="alleen-lezen">
          <CardHeader className="pb-2">
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">Verder in de inhoud <Badge variant="secondary">Alleen lezen</Badge></CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Dit staat in de inhoud van deze pagina (<code>content</code>). {kennis ? 'De code van de site leest het niet. ' : 'De CRM weet niet of de site het toont. '}
              {inhoud.ok ? '' : `${inhoud.reden} `}Het is hier niet te bewerken en blijft bij opslaan letterlijk staan.
            </p>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border p-3 text-xs text-muted-foreground">{JSON.stringify(rest, null, 2)}</pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
